import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ChevronDown, ChevronRight, X, RotateCcw, Plus } from 'lucide-react'
import { Button, Badge, Input } from '@/components/v2/ui'
import { cn } from '@/lib/utils'

interface ForbiddenWordsEditorProps {
  words: string[]
  onAdd: (word: string) => void
  onRemove: (word: string) => void
  onReset: () => void
}

export function ForbiddenWordsEditor({ words, onAdd, onRemove, onReset }: ForbiddenWordsEditorProps) {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)
  const [input, setInput] = useState('')

  const handleAdd = () => {
    if (!input.trim()) return
    onAdd(input)
    setInput('')
  }

  return (
    <div className="rounded-xl border border-neutral-200 dark:border-neutral-800 bg-white dark:bg-neutral-900">
      <button
        type="button"
        aria-expanded={open}
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between p-4 text-sm font-semibold text-neutral-700 dark:text-neutral-200"
      >
        <span>{t('amazonTitleTool.forbiddenWords.title', { count: words.length })}</span>
        {open ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
      </button>
      <div className={cn('overflow-hidden transition-[max-height] duration-300', open ? 'max-h-[500px]' : 'max-h-0')}>
        <div className="px-4 pb-4 space-y-3 border-t border-neutral-100 dark:border-neutral-800 pt-3">
          <div className="flex gap-2">
            <Input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleAdd()}
              placeholder={t('amazonTitleTool.forbiddenWords.addPlaceholder')}
              className="text-sm h-8"
            />
            <Button type="button" size="sm" variant="outline" onClick={handleAdd} disabled={!input.trim()}>
              <Plus className="h-3 w-3" />
            </Button>
            <Button type="button" size="sm" variant="outline" onClick={onReset} title={t('amazonTitleTool.forbiddenWords.resetTitle')}>
              <RotateCcw className="h-3 w-3" />
            </Button>
          </div>
          <div className="flex flex-wrap gap-1 max-h-40 overflow-y-auto">
            {words.map((word) => (
              <Badge key={word} variant="secondary" className="text-xs px-2 py-0.5 flex items-center gap-1">
                {word}
                <button type="button" onClick={() => onRemove(word)} className="hover:text-red-500">
                  <X className="h-3 w-3" />
                </button>
              </Badge>
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}
